import React, { useState } from 'react';
import { t } from '@/locales/strings';
import { hapticTap } from '@/utils/haptics';

/**
 * IntentPicker — first step of the onboarding overlay. Asks the
 * user, in plain language, why she is here. The answer is stored
 * on `user_progress.data.user_intent` and surfaced quietly on the
 * home greeting (and occasionally on /complete) so the practice
 * keeps a thread back to the original reason.
 *
 * Seven options chosen to cover the most common entry paths
 * without over-medicalising:
 *   - postpartum      after a birth
 *   - pre_surgery     ahead of a planned procedure
 *   - intimacy        pleasure / connection
 *   - prevention      general health / future-proofing
 *   - perimenopause   hot flushes, sleep, mood through the transition
 *   - cramps          easing cramps and supporting the menstrual cycle
 *   - noticing        something feels off, exploring why
 *
 * A "skip" path stores `unspecified` so we never gate the rest
 * of onboarding on an answer.
 */

// ── Intent icons ──────────────────────────────────────────────
// Line-style glyphs sharing the 24x24 / 1.6 rose stroke convention
// used elsewhere in onboarding (see PathAssessment.jsx). Each icon
// is a small functional component so the OPTIONS list reads cleanly.

const ICON_PROPS = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

function IconSprout() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 20 V 12" />
      <path d="M12 12 C 8 12, 5 10, 5 6 C 9 6, 12 8, 12 12 Z" />
      <path d="M12 14 C 16 14, 19 12, 19 8 C 15 8, 12 10, 12 14 Z" />
      <path d="M6 20 H 18" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 3 L 19.5 5.5 V 12 C 19.5 16, 16.2 19, 12 21 C 7.8 19, 4.5 16, 4.5 12 V 5.5 Z" />
      <path d="M9 12 L 11 14 L 15 10" />
    </svg>
  );
}

function IconRose() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 13 C 9.5 13, 8 11.5, 8 9.5 C 8 7.5, 10 6, 12 6 C 14 6, 16 7.5, 16 9.5 C 16 11.5, 14.5 13, 12 13 Z" />
      <path d="M12 9.5 C 10.8 9.5, 10 10.3, 10 11" />
      <path d="M12 13 V 21" />
      <path d="M12 17 C 9 17, 7 15, 7 13" />
      <path d="M12 18.5 C 15 18.5, 17 16.5, 17 14.5" />
    </svg>
  );
}

function IconTree() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 4 C 9 4, 7 6.5, 7 9 C 5.5 9.5, 4.5 11, 4.5 12.5 C 4.5 14.5, 6.2 16, 8 16 H 16 C 17.8 16, 19.5 14.5, 19.5 12.5 C 19.5 11, 18.5 9.5, 17 9 C 17 6.5, 15 4, 12 4 Z" />
      <path d="M12 16 V 21" />
    </svg>
  );
}

function IconSun() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="3.6" />
      <path d="M12 3.5 V 5.5" />
      <path d="M12 18.5 V 20.5" />
      <path d="M3.5 12 H 5.5" />
      <path d="M18.5 12 H 20.5" />
      <path d="M5.6 5.6 L 7 7" />
      <path d="M17 17 L 18.4 18.4" />
      <path d="M18.4 5.6 L 17 7" />
      <path d="M7 17 L 5.6 18.4" />
    </svg>
  );
}

function IconDrop() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 3 C 12 3, 6 10, 6 14.5 A 6 6 0 0 0 18 14.5 C 18 10, 12 3, 12 3 Z" />
      <path d="M9 14.5 A 3 3 0 0 0 12 17.5" />
    </svg>
  );
}

function IconMagnifier() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="10.5" cy="10.5" r="5.5" />
      <path d="M14.5 14.5 L 19.5 19.5" />
    </svg>
  );
}

const OPTIONS = [
  { key: 'postpartum',    Icon: IconSprout },
  { key: 'pre_surgery',   Icon: IconShield },
  { key: 'intimacy',      Icon: IconRose },
  { key: 'prevention',    Icon: IconTree },
  { key: 'perimenopause', Icon: IconSun },
  { key: 'cramps',        Icon: IconDrop },
  { key: 'noticing',      Icon: IconMagnifier },
];

export default function IntentPicker({ lang = 'en', onNext, onSkip, onExit }) {
  const [picked, setPicked] = useState(null);
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      background: 'var(--surface, #fff)',
      display: 'flex',
      flexDirection: 'column',
      animation: 'bb-intent-fade 320ms ease-out',
    }}>
      {/* Exit-to-home button. IntentPicker is the FIRST onboarding
          step; bailing here returns the user to /home where the
          "Start Here" CTA can re-launch the flow later. Mirrors the
          exit affordance the rest of the flow gets via OnboardingHeader. */}
      {onExit && (
        <button
          type="button"
          onClick={onExit}
          aria-label={t('exit_onboarding_aria', lang)}
          style={{
            position: 'absolute',
            top: 'max(8px, env(safe-area-inset-top))',
            right: 8,
            zIndex: 2,
            background: 'none',
            border: 'none',
            padding: 12,
            cursor: 'pointer',
            color: 'var(--warm-brown, #8B7355)',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            minHeight: 44,
            minWidth: 44,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        </button>
      )}
      {/* Scrollable content region. flex: 1 + minHeight: 0 lets the
          overflow:auto actually clip inside the fixed parent (without
          minHeight: 0 the flex item refuses to shrink below its
          intrinsic content height and the action row gets pushed off
          screen on shorter viewports). The inner column is centered
          when content fits, scrolls naturally when it doesn't. */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingTop: 'max(48px, env(safe-area-inset-top))',
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 16,
      }}>
        <div style={{ maxWidth: 420, margin: '0 auto', width: '100%' }}>
          <div style={{
            font: '600 10px/1 var(--font-ui)',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: 'var(--primary-dark, var(--primary))',
            marginBottom: 12,
            textAlign: 'center',
          }}>
            {t('intent_eyebrow', lang)}
          </div>
          <h1 style={{
            margin: '0 0 14px',
            font: '500 28px/1.15 var(--font-title)',
            fontStyle: 'italic',
            color: 'var(--ink, #2d1f26)',
            textAlign: 'center',
            textWrap: 'balance',
          }}>
            {t('intent_title', lang)}
          </h1>
          <p style={{
            margin: '0 auto 28px',
            font: '400 14px/1.5 var(--font-body)',
            color: 'var(--ink-dim, #6b5560)',
            textAlign: 'center',
            textWrap: 'balance',
            maxWidth: 320,
          }}>
            {t('intent_subtitle', lang)}
          </p>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
            {OPTIONS.map(({ key, Icon }) => {
              const isPicked = picked === key;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="tap-card"
                    onClick={() => { hapticTap(); setPicked(key); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      width: '100%',
                      padding: '14px 16px',
                      borderRadius: 16,
                      background: isPicked ? 'var(--primary-pale, #fbeae7)' : '#fff',
                      border: isPicked
                        ? '1.5px solid var(--primary, #c97a68)'
                        : '1px solid color-mix(in srgb, var(--ink, #2d1f26) 12%, transparent)',
                      color: 'var(--ink, #2d1f26)',
                      font: 'inherit',
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                      textAlign: 'left',
                      transition: 'background 160ms ease, border-color 160ms ease',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isPicked
                          ? '#fff'
                          : 'color-mix(in srgb, var(--primary, #c97a68) 8%, transparent)',
                        color: 'var(--primary-dark, var(--primary, #c97a68))',
                        transition: 'background 160ms ease',
                      }}
                    >
                      <Icon />
                    </span>
                    <span style={{ flex: 1 }}>
                      {/* Single-line chip. The supporting body copy
                          previously rendered here was making the
                          screen feel like a form; one tap one phrase
                          reads as a check-in instead. The intent_*_body
                          strings stay in the locale file, surfaced
                          via the personalized echo on the path-result
                          screen. */}
                      <div style={{
                        font: '500 16px/1.3 var(--font-title), serif',
                      }}>
                        {t(`intent_${key}_title`, lang)}
                      </div>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Sticky action row. Stays anchored to the bottom of the
          fixed container so Skip / Continue are always reachable, no
          matter how tall the option list is or how short the device
          viewport. The top hairline + soft shadow signal that the
          content above is scrollable. */}
      <div style={{
        flexShrink: 0,
        background: 'var(--surface, #fff)',
        borderTop: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 8%, transparent)',
        boxShadow: '0 -4px 20px -10px rgba(45, 26, 31, 0.08)',
        paddingTop: 12,
        paddingLeft: 24,
        paddingRight: 24,
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
      }}>
        <div style={{
          display: 'flex',
          gap: 10,
          maxWidth: 420,
          margin: '0 auto',
          width: '100%',
        }}>
          <button
            type="button"
            onClick={onSkip}
            style={{
              flex: 1,
              minHeight: 48,
              borderRadius: 999,
              border: '1px solid color-mix(in srgb, var(--ink, #2d1f26) 12%, transparent)',
              background: 'transparent',
              color: 'var(--ink-dim, #6b5560)',
              font: '600 13px/1 var(--font-ui)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('intent_skip', lang)}
          </button>
          <button
            type="button"
            disabled={!picked}
            onClick={() => onNext(picked)}
            style={{
              flex: 1.6,
              minHeight: 48,
              borderRadius: 999,
              border: 'none',
              background: picked
                ? 'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)'
                : 'color-mix(in srgb, var(--primary, #c97a68) 30%, transparent)',
              color: '#fff',
              font: '600 14px/1 var(--font-ui)',
              cursor: picked ? 'pointer' : 'default',
              opacity: picked ? 1 : 0.6,
              WebkitTapHighlightColor: 'transparent',
              transition: 'opacity 160ms ease, background 160ms ease',
            }}
          >
            {t('intent_continue', lang)}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bb-intent-fade {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
